import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { UserStatus, type UserRoleDto } from '../../../api/userManagementApi';
import { useAssignableRoles } from '../../../hooks/useAssignableRoles';
import { useUpdateUserRole } from '../../../hooks/useUpdateUserRole';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { canEditUserRole } from '../../../utils/roleChangePolicy';

interface UserRoleCardProps {
  userId: string;
  roles: UserRoleDto[];
  status: UserStatus;
  isCurrentUser?: boolean;
}

export function UserRoleCard({
  userId,
  roles,
  status,
  isCurrentUser,
}: UserRoleCardProps) {
  const activeRole = roles.length === 1 ? roles[0] : undefined;

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>(
    activeRole?.roleId ?? ''
  );
  const [error, setError] = useState<string | null>(null);

  const {
    assignableRoles,
    canManageAdmins,
    isLoading: rolesLoading,
  } = useAssignableRoles({ enabled: isEditMode });
  const canEditRole = canEditUserRole({
    activeRole,
    status,
    isCurrentUser: !!isCurrentUser,
    canManageAdmins,
  });

  const updateMutation = useUpdateUserRole();

  const handleEdit = () => {
    setIsEditMode(true);
    setSelectedRoleId(activeRole?.roleId ?? '');
    setError(null);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setSelectedRoleId(activeRole?.roleId ?? '');
    setError(null);
  };

  const handleSave = () => {
    if (selectedRoleId === '' || selectedRoleId === activeRole?.roleId) {
      setIsEditMode(false);
      return;
    }

    updateMutation.mutate(
      { userId, roleId: selectedRoleId },
      {
        onSuccess: () => {
          setIsEditMode(false);
          setError(null);
        },
        onError: error => {
          setError(
            error instanceof Error
              ? error.message
              : 'Failed to update user role'
          );
        },
      }
    );
  };

  return (
    <Card variant="outlined" data-testid="user-role-card">
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" component="h3">
              Role
            </Typography>
            {!isEditMode && canEditRole && (
              <PermissionGate
                permission={Permissions.Users.Manage}
                fallback={null}
              >
                <IconButton
                  size="small"
                  onClick={handleEdit}
                  aria-label="Edit role"
                  data-testid="user-role-edit-button"
                  sx={{ p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </PermissionGate>
            )}
          </Box>

          {isEditMode ? (
            <Box data-testid="user-role-edit-mode">
              <Stack spacing={1}>
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <FormControl fullWidth size="small">
                  <InputLabel id="role-select-label">Role</InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={selectedRoleId}
                    label="Role"
                    onChange={e => setSelectedRoleId(e.target.value as number)}
                    disabled={updateMutation.isPending || rolesLoading}
                    data-testid="user-role-select"
                  >
                    {assignableRoles.map(role => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                      updateMutation.isPending ||
                      selectedRoleId === '' ||
                      selectedRoleId === activeRole?.roleId
                    }
                    data-testid="user-role-save-button"
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={updateMutation.isPending}
                    data-testid="user-role-cancel-button"
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Typography
                variant="body2"
                fontWeight={500}
                data-testid="user-role-display"
              >
                {activeRole?.roleName ?? 'No role assigned'}
              </Typography>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
